import axios from 'axios';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 1.25;
const NO_ZOOM = { zoom: 1, x: 0, y: 0 };

const toolBtnStyle = { background: 'rgba(255,255,255,0.1)', border: 'none', boxShadow: 'none', color: 'white', padding: '4px 6px', fontSize: '11px', width: '28px', height: '28px' };

function PreviewFile({ isSingleFile = false, setPreviewVisible, previewFile }) {
    const fileUrl = previewFile?.file_url || "";
    const fileName = previewFile?.file_name || "";

    const [previewFailed, setPreviewFailed] = useState(false);
    const [textContent, setTextContent] = useState('');
    const [tableData, setTableData] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);
    const [rotation, setRotation] = useState(0);
    // Zoom and pan offset (px from centre) of the image preview.
    const [view, setView] = useState(NO_ZOOM);
    const [dragging, setDragging] = useState(false);
    const [boxHeight, setBoxHeight] = useState(null);

    const docxContainerRef = useRef(null);
    const viewportRef = useRef(null);
    const imgRef = useRef(null);
    const dragRef = useRef(null);

    const extension = useMemo(() => {
        return fileUrl
            ?.split('?')[0]
            ?.split('.')
            ?.pop()
            ?.toLowerCase();
    }, [fileUrl]);

    const imageTypes = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'svg'];
    const videoTypes = ['mp4', 'webm', 'ogg', 'mov'];
    const audioTypes = ['mp3', 'wav', 'ogg'];
    const textTypes = ['txt', 'json', 'js', 'html', 'css', 'xml'];
    const officeTypes = ['ppt', 'pptx'];

    useEffect(() => {
        // Reset states
        setPreviewFailed(false);
        setTextContent('');
        setTableData([]);
        setTableColumns([]);
        setRotation(0);
        setView(NO_ZOOM);

        if (!fileUrl) return;

        // ===================== TEXT FILES =====================
        if (textTypes.includes(extension)) {
            axios.get(fileUrl, { responseType: 'text' })
                .then((res) => setTextContent(res.data))
                .catch((err) => {
                    console.error(err);
                    setPreviewFailed(true);
                });
        }

        // ===================== CSV =====================
        if (extension === 'csv') {
            axios.get(fileUrl, { responseType: 'text' })
                .then((res) => {
                    Papa.parse(res.data, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => {
                            const rows = results.data || [];
                            setTableData(rows);
                            if (rows.length > 0) {
                                const cols = Object.keys(rows[0]).map((key) => ({
                                    field: key,
                                    header: key
                                }));
                                setTableColumns(cols);
                            }
                        }
                    });
                })
                .catch((err) => {
                    console.error(err);
                    setPreviewFailed(true);
                });
        }

        // ===================== XLS / XLSX =====================
        if (extension === 'xls' || extension === 'xlsx') {
            axios.get(fileUrl, { responseType: 'arraybuffer' })
                .then((res) => {
                    const workbook = XLSX.read(res.data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet);
                    setTableData(jsonData);
                    if (jsonData.length > 0) {
                        const cols = Object.keys(jsonData[0]).map((key) => ({
                            field: key,
                            header: key
                        }));
                        setTableColumns(cols);
                    }
                })
                .catch((err) => {
                    console.error(err);
                    setPreviewFailed(true);
                });
        }

        // ===================== DOCX =====================
        if (extension === 'docx') {
            axios.get(fileUrl, { responseType: 'arraybuffer' })
                .then(async (res) => {
                    if (docxContainerRef.current) {
                        docxContainerRef.current.innerHTML = '';
                        await renderAsync(res.data, docxContainerRef.current);
                    }
                })
                .catch((err) => {
                    console.error(err);
                    setPreviewFailed(true);
                });
        }
    }, [fileUrl, extension]);

    const isImage = imageTypes.includes(extension);
    const viewRef = useRef(view);
    viewRef.current = view;

    // Keep the pan within the image's edges at the given zoom.
    const clampPan = (x, y, zoom) => {
        const img = imgRef.current;
        if (!img || zoom <= 1) return { x: 0, y: 0 };
        const turned = rotation % 180 !== 0;
        const maxX = ((turned ? img.offsetHeight : img.offsetWidth) * (zoom - 1)) / 2;
        const maxY = ((turned ? img.offsetWidth : img.offsetHeight) * (zoom - 1)) / 2;
        return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
    };

    // Zoom by `factor`, keeping `point` (px from the viewport centre) fixed under the cursor.
    const zoomBy = (factor, point = { x: 0, y: 0 }) => setView(v => {
        const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, v.zoom * factor));
        const ratio = zoom / v.zoom;
        return { zoom, ...clampPan(point.x - (point.x - v.x) * ratio, point.y - (point.y - v.y) * ratio, zoom) };
    });

    const pointFromEvent = (e) => {
        const r = viewportRef.current.getBoundingClientRect();
        return { x: e.clientX - r.left - r.width / 2, y: e.clientY - r.top - r.height / 2 };
    };

    // Wheel zoom needs a non-passive listener to stop the modal scrolling.
    // At 100%, scrolling down is left alone so the page still scrolls.
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (e.deltaY > 0 && viewRef.current.zoom <= MIN_ZOOM) return;
            e.preventDefault();
            zoomBy(e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP, pointFromEvent(e));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [isImage, previewFailed, rotation]);

    // Size the frame to the rotated image so a quarter turn isn't clipped.
    const measureBox = () => {
        const img = imgRef.current;
        if (img) setBoxHeight(rotation % 180 === 0 ? img.offsetHeight : img.offsetWidth);
    };
    useLayoutEffect(measureBox, [rotation]);

    const rotate = (deg) => {
        setRotation(r => (r + deg + 360) % 360);
        setView(v => ({ ...v, x: 0, y: 0 }));
    };

    const onPointerDown = (e) => {
        if (view.zoom <= MIN_ZOOM || e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = { startX: e.clientX, startY: e.clientY, x: view.x, y: view.y };
        setDragging(true);
    };
    const onPointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        setView(v => ({ ...v, ...clampPan(d.x + e.clientX - d.startX, d.y + e.clientY - d.startY, v.zoom) }));
    };
    const endDrag = () => {
        dragRef.current = null;
        setDragging(false);
    };

    const downloadFile = async () => {
        try {
            const response = await axios.get(fileUrl, { responseType: 'blob' });
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'downloaded-file';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            window.open(fileUrl, "_blank");
        }
    };

    useEffect(()=> {
        if(previewFailed && isSingleFile){
            downloadFile()
            setPreviewVisible(false)
        }
    },[previewFailed])
    return (
        <div className='previewFile' style={{ width: "100%" }}>

            {/* Preview Failed */}
            {previewFailed && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    {/* <p>Unable to preview this file.</p> */}
                    <Button label={`Download ${extension.toUpperCase()} File`} icon="pi pi-download" onClick={downloadFile} />
                </div>
            )}

            {/* IMAGES */}
            {!previewFailed && isImage && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Button
                            icon="pi pi-search-minus"
                            title="Zoom Out"
                            onClick={() => zoomBy(1 / ZOOM_STEP)}
                            disabled={view.zoom <= MIN_ZOOM}
                            style={toolBtnStyle}
                        />
                        <Button
                            label={`${Math.round(view.zoom * 100)}%`}
                            title="Reset Zoom"
                            onClick={() => setView(NO_ZOOM)}
                            style={{ ...toolBtnStyle, width: 'auto', minWidth: '48px' }}
                        />
                        <Button
                            icon="pi pi-search-plus"
                            title="Zoom In"
                            onClick={() => zoomBy(ZOOM_STEP)}
                            disabled={view.zoom >= MAX_ZOOM}
                            style={toolBtnStyle}
                        />
                        <span style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
                        <Button
                            icon="pi pi-refresh"
                            title="Rotate Left"
                            onClick={() => rotate(-90)}
                            style={{ ...toolBtnStyle, transform: 'scaleX(-1)' }}
                        />
                        <Button
                            icon="pi pi-refresh"
                            title="Rotate Right"
                            onClick={() => rotate(90)}
                            style={toolBtnStyle}
                        />
                    </div>
                    <div
                        ref={viewportRef}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        onDoubleClick={(e) => (view.zoom > MIN_ZOOM ? setView(NO_ZOOM) : zoomBy(2, pointFromEvent(e)))}
                        style={{
                            width: '100%',
                            height: boxHeight || undefined,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            borderRadius: '8px',
                            touchAction: view.zoom > MIN_ZOOM ? 'none' : 'auto',
                            cursor: view.zoom > MIN_ZOOM ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
                        }}
                    >
                        <img
                            ref={imgRef}
                            src={fileUrl}
                            alt={fileName}
                            draggable={false}
                            onLoad={measureBox}
                            onError={() => setPreviewFailed(true)}
                            style={{
                                maxWidth: rotation % 180 === 0 ? '100%' : '80vh',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom}) rotate(${rotation}deg)`,
                                transition: dragging ? 'none' : 'transform 0.2s ease',
                                userSelect: 'none',
                            }}
                        />
                    </div>
                </div>
            )}

            {/* PDF - Using Simple Iframe (as requested) */}
            {!previewFailed && extension === 'pdf' && (
                <iframe
                    src={fileUrl}
                    title={fileName}
                    width="100%"
                    height="700px"
                    style={{ border: 'none', borderRadius: '8px' }}
                    onError={() => setPreviewFailed(true)}
                />
            )}

            {/* VIDEO */}
            {!previewFailed && videoTypes.includes(extension) && (
                <video
                    controls
                    style={{ width: "100%", maxHeight: "75vh", borderRadius: "8px" }}
                    onError={() => setPreviewFailed(true)}
                >
                    <source src={fileUrl} />
                </video>
            )}

            {/* AUDIO */}
            {!previewFailed && audioTypes.includes(extension) && (
                <audio controls style={{ width: "100%" }} onError={() => setPreviewFailed(true)}>
                    <source src={fileUrl} />
                </audio>
            )}

            {/* TEXT FILES */}
            {!previewFailed && textTypes.includes(extension) && extension !== 'csv' && (
                <div style={{
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '1rem',
                    maxHeight: '700px',
                    overflow: 'auto',
                    background: '#1e1e1e',
                    color: '#fff',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace'
                }}>
                    {textContent}
                </div>
            )}

            {/* CSV & Excel */}
            {!previewFailed && (extension === 'csv' || extension === 'xls' || extension === 'xlsx') && (
                <div style={{ overflow: 'auto' }}>
                    <DataTable
                        value={tableData}
                        paginator
                        rows={10}
                        responsiveLayout="scroll"
                        stripedRows
                        showGridlines
                    >
                        {tableColumns.map((col, index) => (
                            <Column
                                key={index}
                                field={col.field}
                                header={col.header}
                            />
                        ))}
                    </DataTable>
                </div>
            )}

            {/* DOCX */}
            {!previewFailed && extension === 'docx' && (
                <div
                    ref={docxContainerRef}
                    style={{
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '2rem',
                        background: '#fff',
                        overflow: 'auto',
                        maxHeight: '80vh'
                    }}
                />
            )}

            {/* PPT / PPTX */}
            {!previewFailed && officeTypes.includes(extension) && (
                <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                    title={fileName}
                    width="100%"
                    height="700px"
                    style={{ border: 'none', borderRadius: '8px' }}
                    onError={() => setPreviewFailed(true)}
                />
            )}

            {/* UNSUPPORTED */}
            {!previewFailed &&
                !imageTypes.includes(extension) &&
                extension !== 'pdf' &&
                !videoTypes.includes(extension) &&
                !audioTypes.includes(extension) &&
                !textTypes.includes(extension) &&
                !['csv', 'xls', 'xlsx', 'docx'].includes(extension) &&
                !officeTypes.includes(extension) && (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>Preview not available for this file type.</p>
                        <Button label="Download File" icon="pi pi-download" onClick={downloadFile} />
                    </div>
                )}
        </div>
    );
}

export default PreviewFile;